/**
 * Rendering for the assistant's markdown replies.
 *
 * Replaces the demo's hand-rolled `md(s)` (source line 4032) — thirty lines of
 * string surgery that understood `##`, `**bold**`, `` `code` ``, `- ` bullets
 * and pipe tables — with the `marked` parser this repo already depends on and
 * already configures for the build-doc corpus (`src/shared/build-docs.ts`).
 *
 * ## `marked` for parsing, JSX for rendering
 *
 * `marked` is used as a **lexer only**. Its `lexer()` returns a token tree;
 * this module walks that tree and returns React elements. Nothing is turned
 * into an HTML string and nothing reaches `dangerouslySetInnerHTML`, which is
 * what `npm run check:oem-handlers` exists to prevent: ported demo content is
 * modelled as typed blocks and rendered as JSX. It also means a reply can
 * never inject an element, even if a future branch interpolates user text into
 * a sentence — React escapes every text node by construction.
 *
 * The dialect `local-answer.ts` emits is a strict subset of GitHub-flavoured
 * markdown: bold, inline code, bullet lists and pipe tables with a `|---|`
 * separator row. Every token type below covers that subset plus the few extras
 * (headings, blockquotes, fenced code, links) a future branch might reach for.
 * Anything `marked` produces that is not handled falls back to its raw text,
 * so an unknown token degrades to plain prose rather than disappearing.
 *
 * ## Styling
 *
 * Tailwind classes on the elements themselves, not a global stylesheet. The
 * chat rail is 25% of the viewport, so the table rule matters most: small type,
 * a horizontal scroll container that says so at its edges and takes keyboard
 * focus ({@link TableScroller}), and the same hairline and ink tokens the rest
 * of the chrome uses. No hex values, and the one global class it borrows
 * (`scroll-soft`) already exists in `src/styles/global.css`.
 *
 * UI layer (layer 4 of the 4-layer architecture).
 *
 * @see ./local-answer.ts — what produces the markdown
 * @see src/shared/build-docs.ts — the other `Marked` instance in this repo
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Marked, type Token, type Tokens } from 'marked';

/**
 * A local `Marked` instance, exactly as `build-docs.ts` does it — the global
 * `marked` singleton is used by `ChangelogScene` and must stay untouched.
 *
 * `gfm` gives pipe tables; `breaks` is off because the branches separate
 * paragraphs with a blank line, as the demo's did.
 */
const md = new Marked({ gfm: true, breaks: false });

/** Parse markdown to a token tree. Exported for tests and the Tier 3 live mode. */
export function lexAnswerMarkdown(source: string): Token[] {
  return md.lexer(source);
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

const CODE_CLASS =
  'mono rounded border border-[var(--hairline)] bg-[var(--surface-2)] px-1 py-px text-[12px]';

function Inline({ tokens }: { tokens: readonly Token[] | undefined }): ReactNode {
  if (!tokens) return null;
  return (
    <>
      {tokens.map((t, i) => (
        <Fragment key={i}>{inlineNode(t)}</Fragment>
      ))}
    </>
  );
}

function inlineNode(token: Token): ReactNode {
  switch (token.type) {
    case 'strong':
      return (
        <strong className="font-semibold">
          <Inline tokens={(token as Tokens.Strong).tokens} />
        </strong>
      );
    case 'em':
      return (
        <em className="italic">
          <Inline tokens={(token as Tokens.Em).tokens} />
        </em>
      );
    case 'del':
      return (
        <del>
          <Inline tokens={(token as Tokens.Del).tokens} />
        </del>
      );
    case 'codespan':
      return <code className={CODE_CLASS}>{(token as Tokens.Codespan).text}</code>;
    case 'br':
      return <br />;
    case 'link': {
      const t = token as Tokens.Link;
      return (
        <a
          href={t.href}
          className="text-accent-600 underline underline-offset-2 dark:text-accent-400"
        >
          <Inline tokens={t.tokens} />
        </a>
      );
    }
    case 'text': {
      const t = token as Tokens.Text;
      // A `text` token carries nested inline tokens only inside a list item.
      return t.tokens?.length ? <Inline tokens={t.tokens} /> : t.text;
    }
    case 'escape':
      return (token as Tokens.Escape).text;
    default:
      return 'text' in token ? (token as { text: string }).text : null;
  }
}

// ---------------------------------------------------------------------------
// Table scroller
// ---------------------------------------------------------------------------

/** How far the fade reaches in from an edge that has more table behind it. */
const FADE = '24px';

/**
 * The horizontal scroll container a wide table sits in.
 *
 * The rail is a quarter of the viewport — 273px of content at 1280 — and a
 * cost table is six currency columns, about 490px at the tightest padding
 * that stays legible. It cannot be made to fit, so it scrolls, and it always
 * did. What was missing is any way to know that: macOS draws overlay
 * scrollbars, which appear only mid-gesture and reserve no space, so the
 * clipped last column read as a layout bug rather than as more table. No CSS
 * can override that — `scrollbar-width` and `::-webkit-scrollbar` both leave
 * the gutter at zero — so the affordance has to live in the content.
 *
 * Each edge with more table behind it fades out. A mask rather than a
 * gradient overlay, because a mask needs no opinion about what colour is
 * behind it: the rail is a translucent glass surface over whatever the scene
 * is painting, and a matched-colour gradient would band in one theme or the
 * other. `black` and `transparent` here are mask alpha, not ink.
 *
 * `scroll-soft` is kept for the platforms that do reserve a gutter, where it
 * draws the 6px token-coloured bar the rest of the chrome uses.
 */
function TableScroller({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // A one-pixel slack: fractional scroll positions would otherwise leave the
    // fade flickering on at the far end of the track.
    const max = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft > 1;
    const right = max > 1 && el.scrollLeft < max - 1;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // The rail resizes when it opens, closes and when the window does, and the
    // table's own width changes with it — both change whether it can scroll.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const mask =
    edges.left || edges.right
      ? `linear-gradient(to right, transparent 0, black ${edges.left ? FADE : '0'}, ` +
        `black calc(100% - ${edges.right ? FADE : '0px'}), transparent 100%)`
      : undefined;

  return (
    <div
      ref={ref}
      onScroll={measure}
      className="scroll-soft my-2.5 overflow-x-auto"
      // A region that scrolls has to be reachable without a mouse (WCAG 2.1.1),
      // and once it can take focus it needs a name.
      role="region"
      aria-label="Table — scrolls horizontally"
      tabIndex={0}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

function Blocks({ tokens }: { tokens: readonly Token[] }): ReactNode {
  return (
    <>
      {tokens.map((t, i) => (
        <Fragment key={i}>{blockNode(t, i === 0)}</Fragment>
      ))}
    </>
  );
}

function blockNode(token: Token, first: boolean): ReactNode {
  switch (token.type) {
    case 'space':
      return null;

    case 'paragraph':
      return (
        <p className={first ? 'mb-2' : 'my-2'}>
          <Inline tokens={(token as Tokens.Paragraph).tokens} />
        </p>
      );

    case 'heading': {
      const t = token as Tokens.Heading;
      const size = t.depth <= 1 ? 'text-[15px]' : 'text-sm';
      return (
        <p className={`${first ? 'mb-1.5' : 'mt-3 mb-1.5'} ${size} font-semibold`}>
          <Inline tokens={t.tokens} />
        </p>
      );
    }

    case 'list': {
      const t = token as Tokens.List;
      const cls = 'my-2 space-y-1 pl-5 marker:text-zinc-400';
      const items = t.items.map((item, i) => (
        <li key={i}>
          <Inline tokens={item.tokens} />
        </li>
      ));
      return t.ordered ? (
        <ol className={`list-decimal ${cls}`}>{items}</ol>
      ) : (
        <ul className={`list-disc ${cls}`}>{items}</ul>
      );
    }

    case 'table': {
      const t = token as Tokens.Table;
      return (
        <TableScroller>
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr>
                {t.header.map((cell, i) => (
                  <th
                    key={i}
                    scope="col"
                    className="whitespace-nowrap border-b border-[var(--hairline)] bg-[var(--surface-2)] px-2 py-1.5 text-left font-semibold"
                    style={t.align[i] ? { textAlign: t.align[i] ?? undefined } : undefined}
                  >
                    <Inline tokens={cell.tokens} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className="border-b border-[var(--hairline)] px-2 py-1.5 align-top last:border-b-0"
                      style={t.align[c] ? { textAlign: t.align[c] ?? undefined } : undefined}
                    >
                      <Inline tokens={cell.tokens} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      );
    }

    case 'code':
      return (
        <pre className="my-2 overflow-x-auto rounded-md border border-[var(--hairline)] bg-[var(--surface-2)] p-3 text-[12px]">
          <code className="mono">{(token as Tokens.Code).text}</code>
        </pre>
      );

    case 'blockquote':
      return (
        <blockquote className="my-2 border-l-2 border-[var(--accent-soft)] pl-3 text-muted">
          <Blocks tokens={(token as Tokens.Blockquote).tokens} />
        </blockquote>
      );

    case 'hr':
      return <hr className="my-3 border-[var(--hairline)]" />;

    default:
      // Unknown token: degrade to its raw text rather than dropping it.
      return 'text' in token ? (
        <p className="my-2">{(token as { text: string }).text}</p>
      ) : null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** The assistant's reply body, rendered from markdown as JSX. */
export function AssistantMarkdown({ source }: { source: string }) {
  const tokens = useMemo(() => lexAnswerMarkdown(source), [source]);
  return (
    <div className="min-w-0 text-sm leading-relaxed text-zinc-900 dark:text-zinc-100">
      <Blocks tokens={tokens} />
    </div>
  );
}
